import { TriangleAlert } from 'lucide-react';

import countdownChip from '@/assets/icons/countdown-chip.svg';
import { Button } from '@/components/ui/button';
import { NotificationBanner } from '@/components/ui/notification-banner/notification-banner';
import {
  formatCurrencyAmount,
  openPaymentUrl,
  useOfferCountdown,
  useScheduledPaymentsStatus,
} from '@/features/payments';
import type { OfferCountdown } from '@/features/payments';
import type { ProfilesResponse } from '@/features/user';
import { useAnalytics } from '@/lib/analytics';

/**
 * Global payment-status banner — checks every course of the selected
 * profile (the selected course first), regardless of which course is
 * currently selected. Shows one of three states for the first course with a
 * payment due:
 *
 * 1. Offer running (purple): trial-ending countdown to `offer.expires_at`,
 *    struck-through total, discounted payable amount, and savings copy.
 * 2. Offer expired / absent (red): "trial has ended" copy with the full
 *    amount to reactivate.
 * 3. Legacy (red): the pre-existing banner, for every other payment-due
 *    course — grouped_schedule_status !== CREATED, or amounts missing.
 *
 * States 1 and 2 are responsive: below `md` they collapse into a compact
 * price-led row (short copy, single-line countdown, inline CTA); `md` and up
 * renders the full Figma layout.
 */
export function PaymentStatusBanner() {
  const { isPaymentPending, courseName, paymentUrl, metadata } =
    useScheduledPaymentsStatus();

  if (!isPaymentPending) {
    return null;
  }

  const amountTotal = formatCurrencyAmount(
    metadata?.amount_total?.value,
    metadata?.currency
  );

  // The redesigned states need a renderable total; anything short of that
  // (legacy payload, partial rollout) falls back to the legacy banner.
  if (
    !metadata ||
    metadata.grouped_schedule_status !== 'CREATED' ||
    amountTotal === null
  ) {
    return (
      <LegacyPaymentBanner courseName={courseName} paymentUrl={paymentUrl} />
    );
  }

  return (
    <TrialPaymentBanner
      metadata={metadata}
      amountTotal={amountTotal}
      paymentUrl={paymentUrl}
    />
  );
}

function LegacyPaymentBanner({
  courseName,
  paymentUrl,
}: Readonly<{
  courseName: string | null;
  paymentUrl: string | null;
}>) {
  const { trackEvent } = useAnalytics();

  // The course name is optional copy — when it's missing we still surface
  // the banner with a generic message.
  const title = courseName
    ? `Your ${courseName} Course has a pending payment. Complete it to continue your sessions.`
    : 'You have a pending payment. Complete it to continue your sessions.';

  return (
    <NotificationBanner
      variant="error"
      size="sm"
      title={title}
      action={
        <Button
          variant="outline"
          size="sm"
          color="light"
          onClick={() =>
            openPaymentUrl(paymentUrl, {
              source: 'paymentBanner',
              trackEvent,
            })
          }
        >
          Complete Payment
        </Button>
      }
    />
  );
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Compact one-line countdown for the mobile banner, e.g. "2d 03:44:59". */
const formatCompactCountdown = (countdown: OfferCountdown): string => {
  const clock = `${pad(countdown.hours)}:${pad(countdown.minutes)}:${pad(
    countdown.seconds
  )}`;
  return countdown.days > 0 ? `${countdown.days}d ${clock}` : clock;
};

function TrialPaymentBanner({
  metadata,
  amountTotal,
  paymentUrl,
}: Readonly<{
  metadata: ProfilesResponse.EnrolledCourseMetadata;
  amountTotal: string;
  paymentUrl: string | null;
}>) {
  const { trackEvent } = useAnalytics();
  const countdown = useOfferCountdown(metadata.offer?.expires_at);
  const amountPayable = formatCurrencyAmount(
    metadata.amount_payable?.value,
    metadata.currency
  );
  const savings = formatCurrencyAmount(
    metadata.offer?.value,
    metadata.currency
  );

  // Without a renderable discounted amount there is nothing to advertise, so
  // an offer that can't be displayed behaves like an expired one.
  const isOfferActive =
    !!metadata.offer && !countdown.isExpired && amountPayable !== null;

  // Mobile shows a small bullet (the compact row has no room for the 24px
  // triangle); md+ shows the design's warning triangle.
  const icon = (
    <>
      <span
        className="mt-2 block size-2 shrink-0 rounded-full bg-white/60 md:hidden"
        aria-hidden="true"
      />
      <TriangleAlert
        className="hidden h-6 w-6 shrink-0 text-white md:block"
        aria-hidden="true"
      />
    </>
  );

  // Both CTAs report the variant the student actually saw, so the countdown
  // offer's conversion can be compared against the expired banner. Mobile vs
  // desktop is already derivable from the viewport dimensions `useAnalytics`
  // attaches, so it isn't duplicated here.
  const openCheckout = () =>
    openPaymentUrl(paymentUrl, {
      source: isOfferActive ? 'trialOfferBanner' : 'trialExpiredBanner',
      trackEvent,
    });

  // CTAs live inside the responsive title branches (not the action slot) so
  // the mobile row keeps its button inline on the right.
  const payNowMobile = (
    <Button
      variant="outline"
      size="sm"
      color="light"
      className="shrink-0"
      onClick={openCheckout}
    >
      Pay Now
    </Button>
  );
  const payNowDesktop = (
    <Button variant="outline" size="md" color="light" onClick={openCheckout}>
      Pay Now
    </Button>
  );

  if (!isOfferActive) {
    return (
      <NotificationBanner
        variant="error"
        size="sm"
        shimmer
        className="sm:items-center md:min-h-[60px]"
        icon={icon}
        title={
          <div className="min-w-0">
            {/* Mobile: compact price-led row with trimmed copy */}
            <div className="flex items-center justify-between gap-3 md:hidden">
              <div className="min-w-0">
                <p className="text-lg leading-6 font-bold">{amountTotal}</p>
                <p className="text-xs leading-4 font-bold text-white/80">
                  Trial ended · reactivate to continue
                </p>
              </div>
              {payNowMobile}
            </div>
            {/* Desktop: full copy with the amount emphasized inline */}
            <div className="hidden items-center gap-6 md:flex">
              <p className="text-base leading-6 font-bold break-words">
                Your Course trial has ended. Pay{' '}
                <span className="text-2xl leading-8 whitespace-nowrap">
                  {amountTotal}
                </span>{' '}
                to reactivate
              </p>
              {payNowDesktop}
            </div>
          </div>
        }
      />
    );
  }

  return (
    <NotificationBanner
      variant="info"
      size="sm"
      shimmer
      className="bg-purplish-blue sm:items-center md:min-h-[60px]"
      icon={icon}
      title={
        <div className="min-w-0">
          {/* Mobile: compact price-led row with a one-line countdown */}
          <div className="flex items-center justify-between gap-3 md:hidden">
            <div className="min-w-0">
              <p className="flex flex-wrap items-baseline gap-x-2 font-bold">
                <span className="text-lg leading-6">{amountPayable}</span>
                <span className="text-xs leading-4 text-white/70 line-through">
                  {amountTotal}
                </span>
                {savings ? (
                  <span className="text-xs leading-4">Save {savings}</span>
                ) : null}
              </p>
              <p className="text-xs leading-4 font-bold text-white/80">
                Trial ending in{' '}
                {/* Ticks every second — keep it out of the live region. */}
                <span aria-hidden="true">
                  {formatCompactCountdown(countdown)}
                </span>
                <span className="sr-only">— limited-time offer</span>
              </p>
            </div>
            {payNowMobile}
          </div>
          {/* Desktop: Figma layout — copy, countdown chips, prices, CTA */}
          <div className="hidden flex-wrap items-center gap-x-6 gap-y-2 md:flex">
            <span className="text-base leading-6 font-bold">
              Your Trial Ending in
            </span>
            <OfferCountdownChips countdown={countdown} />
            <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <span className="text-base leading-6 font-bold text-white line-through opacity-55">
                {amountTotal}
              </span>
              <span className="text-2xl leading-8 font-bold whitespace-nowrap">
                {amountPayable}
              </span>
              {savings ? (
                <span className="text-base leading-6 font-bold">
                  Pay now and save {savings}
                </span>
              ) : null}
            </span>
            {payNowDesktop}
          </div>
        </div>
      }
    />
  );
}

function OfferCountdownChips({
  countdown,
}: Readonly<{ countdown: OfferCountdown }>) {
  const parts = [
    { value: String(countdown.days), unit: 'd' },
    { value: pad(countdown.hours), unit: 'h' },
    { value: pad(countdown.minutes), unit: 'm' },
    { value: pad(countdown.seconds), unit: 's' },
  ];

  return (
    <span className="flex items-center gap-[2px]">
      {/* Static for screen readers: the banner is an aria-live alert, so the
          per-second tick must stay out of the accessibility tree or it would
          re-announce the whole banner every second. */}
      <span className="sr-only">Limited-time offer countdown running.</span>
      <span aria-hidden="true" className="flex items-center gap-[2px]">
        {parts.map((part, index) => (
          <span key={part.unit} className="flex items-center gap-[2px]">
            {index > 0 ? <ChipSeparator /> : null}
            <span className="relative block h-[38px] w-[34px] shrink-0">
              <img
                src={countdownChip}
                alt=""
                width={34}
                height={38}
                className="absolute inset-0 block h-full w-full"
              />
              <span className="absolute inset-x-0 top-1 bottom-0 flex items-baseline justify-center pt-[6px]">
                <span className="text-sm leading-[22px] font-bold text-white">
                  {part.value}
                </span>
                <span className="text-xs leading-5 font-bold text-white">
                  {part.unit}
                </span>
              </span>
            </span>
          </span>
        ))}
      </span>
    </span>
  );
}

function ChipSeparator() {
  return (
    <span className="flex flex-col items-center gap-[3px] px-[2px]">
      <span className="h-1 w-1 rounded-full bg-black/30" />
      <span className="h-1 w-1 rounded-full bg-black/30" />
    </span>
  );
}
